import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import TuneIcon from "@mui/icons-material/Tune";

import {
  ChartScales,
  ChartScalesType,
  ChartTimeframes,
  ChartTimeframesType,
} from "./Enums";
import {
  Button,
  ButtonProps,
  Divider,
  FormControlLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import { useAppContext } from "../context/AppContext";

const getFrom = (key: Exclude<ChartTimeframesType, "alltime">) => {
  const date = new Date();
  switch (key) {
    case ChartTimeframes.year:
      return new Date(date.setFullYear(date.getFullYear() - 1));
    case ChartTimeframes.halfyear:
      return new Date(date.setMonth(date.getMonth() - 6));
    case ChartTimeframes.quarter:
      return new Date(date.setMonth(date.getMonth() - 3));
    case ChartTimeframes.month:
      return new Date(date.setMonth(date.getMonth() - 1));
    case ChartTimeframes.week:
      return new Date(date.setDate(date.getDate() - 7));
    case ChartTimeframes.day:
      return new Date(date.setDate(date.getDate() - 1));
    default:
      return null;
  }
};

export const ChartFineTuneButton: React.FC<
  ButtonProps & {
    onChange: (timeframe: { from: Date | null; to: Date | null }) => void;
    showNight: boolean;
    setShowNight: Dispatch<SetStateAction<boolean>>;
    showMidnight: boolean;
    setShowMidnight: Dispatch<SetStateAction<boolean>>;
  }
> = ({
  onChange,
  showNight,
  setShowNight,
  showMidnight,
  setShowMidnight,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const { chartConfig, setChartConfig } = useAppContext();

  const ref = useRef(null);

  const [open, setOpen] = useState(false);

  const onChangeScale = useCallback(
    (scale: ChartScalesType) => () => {
      setChartConfig((s) => ({ ...s, scale }));
      setOpen(false);
    },
    [setChartConfig]
  );

  const chartTimeframeValues = Object.values(ChartTimeframes);

  const onChangeTimeframe = (key: ChartTimeframesType) => () => {
    // Perform type casting once
    const castedKey = key as ChartTimeframes;

    // Use the cached values for the check
    if (chartTimeframeValues.includes(castedKey)) {
      const from = getFrom(
        castedKey as Exclude<ChartTimeframesType, "alltime">
      );
      // Only create a new Date object when necessary
      onChange({ from, to: new Date() });
    }
    setOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        ref={ref}
        variant="outlined"
        {...buttonProps}
      >
        <TuneIcon sx={{ mr: { md: 1 } }} />
        <Typography noWrap sx={{ display: { xs: "none", md: "inline-block" } }}>
          {t("tabs.chart.settings-button")}
        </Typography>
      </Button>
      <Menu
        anchorEl={ref.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{ dense: true }}
        slotProps={{ paper: { sx: { maxWidth: 300 } } }}
      >
        <ListSubheader>{t("tabs.chart.time-label")}</ListSubheader>
        {Object.keys(ChartTimeframes).map((key) => (
          <MenuItem
            key={key}
            onClick={onChangeTimeframe(key as ChartTimeframesType)}
          >
            {t(`tabs.chart.time-${key}-option`)}
          </MenuItem>
        ))}

        <Divider />
        <ListSubheader>{t("tabs.chart.scale-label")}</ListSubheader>
        {Object.keys(ChartScales).map((key) => (
          <MenuItem
            key={key}
            onClick={onChangeScale(key as ChartScalesType)}
            selected={key === chartConfig.scale}
          >
            <ListItemText
              primary={t(`tabs.chart.scale-${key}-option`)}
              primaryTypographyProps={{ gutterBottom: true }}
              secondary={t(`tabs.chart.scale-${key}-option-text`)}
              secondaryTypographyProps={{
                variant: "caption",
                noWrap: false,
                style: { whiteSpace: "normal" },
              }}
            />
          </MenuItem>
        ))}
        <Divider />
        <ListSubheader>{t("tabs.chart.show-nighttime-label")}</ListSubheader>
        <MenuItem sx={{ pl: 0 }}>
          <FormControlLabel
            onChange={() => {
              setShowNight((val) => !val);
              setOpen(false);
            }}
            control={<Switch checked={showNight} tabIndex={-1} />}
            value={showNight}
            labelPlacement="start"
            disableTypography
            label={
              <ListItemText
                primary={t("tabs.chart.show-nighttime")}
                primaryTypographyProps={{ gutterBottom: true }}
                secondary={t("tabs.chart.show-nighttime-secondary")}
                secondaryTypographyProps={{
                  variant: "caption",
                  noWrap: false,
                  style: { whiteSpace: "normal" },
                }}
              />
            }
          />
        </MenuItem>
        <MenuItem sx={{ pl: 0 }}>
          <FormControlLabel
            onChange={() => {
              setShowMidnight((val) => !val);
              setOpen(false);
            }}
            control={<Switch checked={showMidnight} tabIndex={-1} />}
            value={showMidnight}
            labelPlacement="start"
            disableTypography
            label={
              <ListItemText
                primary={t("tabs.chart.show-midnight")}
                primaryTypographyProps={{ gutterBottom: true }}
                secondary={t("tabs.chart.show-midnight-secondary")}
                secondaryTypographyProps={{
                  variant: "caption",
                  noWrap: false,
                  style: { whiteSpace: "normal" },
                }}
              />
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};
